import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Truck, Landmark, Wrench } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { PRODUCTS, PRODUCT_TABS, IMAGES } from "@/data/site";

const Hero = () => (
  <section className="blueprint-grid" data-testid="products-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-14">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <Overline>Products</Overline>
          <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink">
            <MaskedLine delay={0.15}>Genuine hardware.</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-cobalt italic">Corporate terms.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed">
              Laptops, desktops, printers, UPS, sound systems and interactive panels — sourced from authorised channels with on-site warranty and bulk procurement support.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.3} className="lg:col-span-4">
          <div className="rounded-3xl bg-white border border-gray-200 p-6 flex items-center gap-4" data-testid="products-stock-badge">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
            <div>
              <p className="font-display font-bold text-ink text-sm">Stock available in NCR</p>
              <p className="text-xs text-gray-500 mt-0.5">Pan-India delivery & installation</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Catalog = () => {
  const [tab, setTab] = useState("all");
  const filtered = tab === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === tab);

  return (
    <section className="py-16 sm:py-20" data-testid="products-catalog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-2" data-testid="products-tab-group">
          {PRODUCT_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`products-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                tab === t.id ? "bg-ink text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-cobalt hover:text-cobalt"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className="group rounded-3xl overflow-hidden border border-gray-200 bg-white flex flex-col transition-shadow duration-300 hover:shadow-xl hover:shadow-gray-200"
                data-testid={`product-card-${p.id}`}
              >
                <div className="overflow-hidden h-52 relative">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] text-gray-700">
                    {PRODUCT_TABS.find((t) => t.id === p.category)?.label}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-bold tracking-tight text-ink">{p.name}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{p.blurb}</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-[13px] text-gray-600">
                        <CheckCircle2 className="h-4 w-4 text-cobalt shrink-0 mt-0.5" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/contact?product=${p.id}`}
                    data-testid={`product-rfq-${p.id}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-cobalt transition-colors duration-300"
                  >
                    Request bulk quote
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

const Procurement = () => (
  <section className="py-20 bg-ink text-white relative grain overflow-hidden" data-testid="products-procurement">
    <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Overline light>Procurement, simplified</Overline>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            One PO for the whole office refresh.
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed max-w-lg">
            Whether it is five laptops or a full floor fit-out, we consolidate sourcing, imaging, delivery, installation and warranty under a single accountable order.
          </p>
          <div className="mt-9 space-y-5">
            {[
              { icon: Truck, t: "Delivered & installed", d: "Pan-India logistics with on-site setup, imaging and network joining." },
              { icon: Landmark, t: "Bulk & financing support", d: "Corporate billing, volume pricing and assistance with procurement financing." },
              { icon: Wrench, t: "Warranty & AMC", d: "OEM warranty management plus annual maintenance contracts for uptime." },
            ].map((f, i) => (
              <div key={f.t} className="flex items-start gap-4" data-testid={`procurement-feature-${i}`}>
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cobalt">
                  <f.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold">{f.t}</h3>
                  <p className="mt-1 text-sm text-white/60 leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/contact?category=hardware"
            data-testid="procurement-cta"
            className="mt-10 inline-flex items-center gap-2 bg-white text-ink font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-cobalt hover:text-white transition-colors duration-300"
          >
            Send your requirement list
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <img src={IMAGES.panel} alt="Interactive smart display in a modern meeting room" className="w-full h-[460px] object-cover" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Products() {
  return (
    <div data-testid="products-page">
      <Hero />
      <Catalog />
      <Procurement />
    </div>
  );
}
